import type {Relays} from './types.ts';
import {IOGateway} from '@interopio/gateway';
import type {WebSocket} from 'ws';

type Command
    = { type: 'hello', 'from': string, to: string }
    | { type: 'bye', 'from': string, to: 'all' | string }
    | { type: 'data', from: string, to: 'all' | string, data: { body: { type: string } } }

    ;

const codec = IOGateway.Encoding.transit<Command>({
    keywordize: new Map<string, IOGateway.Encoding.KeywordizeCommand>([
        ['/type', '*'],
        ['/message/body/type', '*'],
        ['/message/origin', '*'],
        ['/message/receiver/type', '*'],
        ['/message/source/type', '*'],
        ['/message/body/type', '*'],
    ])
});

export class InternalRelays implements Relays {
    readonly #logger: IOGateway.Logging.Logger;
    // key -> socket
    readonly #clients = new Map<string, WebSocket>();
    // node -> key
    readonly #links = new Map<string, string>();
    public onMsg?: (key: string, node: string, msg: string) => void
    public onErr?: (key: string, err: Error) => void

    constructor(logger: IOGateway.Logging.Logger) {
        this.#logger = logger;
    }

    add(key: string, soc: WebSocket) {
        this.#clients.set(key, soc);
    }

    remove(key: string) {
        this.#clients.delete(key);
        for (const [node, k] of this.#links) {
            if (k === key) {
                this.#links.delete(node);
            }
        }
    }

    receive(key: string, msg: string) {
        const node = this.link(key, msg);

        if (node && this.onMsg) {
            this.onMsg(key, node, msg);
        }
    }

    private link(key: string, msg: string): string | undefined {
        try {
            const decoded = codec.decode(msg);
            const {type, from, to} = decoded;
            if (to === 'all') {
                switch (type) {
                    case 'hello': {
                        if (this.#logger.enabledFor('debug')) {
                            this.#logger.debug(`${key} registers node ${from}`);
                        }
                        this.#links.set(from, key);
                        break;
                    }
                    case 'bye': {
                        if (this.#logger.enabledFor('debug')) {
                            this.#logger.debug(`${key} unregisters node ${from}`);
                        }
                        this.#links.delete(from);
                        break;
                    }
                }
                return;
            }
            return from;
        } catch (e) {
            if (this.onErr) {
                // probably a decode error or a bug
                this.onErr(key, e instanceof Error ? e : new Error(`link failed :${e}`));
            } else {
                this.#logger.warn(`${key} unable to process ${msg}`, e);
            }
        }
    }

    send(key: string, node: string, msg: string, cb: (k: string, err?: Error) => void): void {
        const decoded = codec.decode(msg);
        if (this.#logger.enabledFor('debug')) {
            this.#logger.debug(`${key} sending msg to ${node} ${JSON.stringify(decoded)}`);
        }

        const clientKey = this.#links.get(node);
        if (clientKey) {
            const client = this.#clients.get(clientKey);
            if (client) {
                client.send(msg, {binary: false}, (err?: Error) => {
                    cb(clientKey, err)
                });
                return;
            }
        }
        throw new Error(`${key} no active link for ${decoded.to}`);
    }
}
